import React from 'react';
import { Box, Collapse, List, Typography } from '@mui/material';
import type { NavigationItem } from '../navigationItems';
import { useNavItemVisible } from '../../../hooks/useNavItemVisible';
import { SidebarNavItem } from './SidebarNavItem';

interface SidebarNavGroupProps {
  item: NavigationItem;
  permissions: string[];
  isActive: (path: string) => boolean;
  isCollapsed: boolean;
  onToggleCollapsed: (group: string) => void;
  onNavigate: (item: NavigationItem) => void;
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  item,
  permissions,
  isActive,
  isCollapsed,
  onToggleCollapsed,
  onNavigate,
}) => {
  const visible = useNavItemVisible(item, permissions);
  if (!visible) return null;

  // Hide group if no sub-items are permitted
  const visibleSubItems = item.subItems?.filter(sub =>
    sub.permission ? permissions.includes(sub.permission) : true
  );
  if (!visibleSubItems?.length) return null;

  return (
    <>
      <Box
        onClick={() => onToggleCollapsed(item.text)}
        data-testid={`expanded-nav-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          pl: 2,
          pr: 1,
          mt: 1.5,
          mb: 0.25,
          userSelect: 'none',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {item.text}
        </Typography>
        <Box
          component="span"
          sx={{
            ml: 0.5,
            color: 'text.tertiary',
            transform: isCollapsed ? 'rotate(-90deg)' : 'none',
            transition: 'transform 0.2s',
            display: 'inline-flex',
          }}
        >
          ▾
        </Box>
      </Box>
      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <List disablePadding>
          {item.subItems!.map(subItem => (
            <SidebarNavItem
              key={subItem.text}
              item={subItem}
              permissions={permissions}
              isActive={isActive(subItem.path)}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};
